import { supabase } from '@/integrations/supabase/client';
import { ensureServiceWorkerRegistration, urlBase64ToUint8Array, vapidPublicKey } from './common';

const REFRESHED_AT_KEY = 'push-subscription-refreshed-at';
const REFRESH_INTERVAL_MS = 12 * 60 * 60 * 1000;

/** Personal staff id, or null for a location account / unlinked login. */
async function findPersonalStaffId(): Promise<string | null> {
  const { data, error } = await supabase.rpc('get_my_staff');
  if (error) throw error;
  return data?.[0]?.id ?? null;
}

async function getPersonalStaffId(): Promise<string> {
  const staffId = await findPersonalStaffId();
  if (!staffId) {
    throw new Error('push: no staff identity — location accounts cannot subscribe');
  }
  return staffId;
}

export async function thisDeviceSubscribed(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
  if (!registration) return false;
  const sub = await registration.pushManager.getSubscription();
  return sub != null;
}

export async function subscribeThisDevice(): Promise<void> {
  const key = vapidPublicKey();
  await getPersonalStaffId();

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('push: this browser cannot subscribe to Web Push');
  }
  if (typeof Notification === 'undefined') {
    throw new Error('push: Notification API is not available');
  }
  if (Notification.permission === 'denied') {
    throw new Error('push: notification permission denied');
  }
  if (Notification.permission !== 'granted') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') {
      throw new Error('push: notification permission denied');
    }
  }

  const registration = await ensureServiceWorkerRegistration();

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
  });
  const json = subscription.toJSON();
  const endpoint = json.endpoint;
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!endpoint || !p256dh || !auth) {
    throw new Error('push: subscription is missing endpoint or keys');
  }

  const { error } = await supabase.rpc('push_subscribe' as never, {
    p_endpoint: endpoint,
    p_p256dh: p256dh,
    p_auth: auth,
    p_user_agent: navigator.userAgent,
  } as never);
  if (error) throw error;
}

/**
 * Re-posts an existing personal staff subscription so the server can refresh
 * last_seen_at and learn rotated endpoint keys. This background path must not
 * prompt for permission or create a subscription, and runs only when the
 * signed-in staff member already owns this endpoint's push_subscriptions row
 * (RLS push_subscriptions_select_own), never for a location device.
 */
export async function refreshThisDeviceSubscription(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

    const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
    if (!registration) return;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;

    // A location account (floor iPad JWT) has no personal staff identity and
    // never refreshes a personal subscription: return silently, before the
    // push_subscriptions lookup or push_subscribe, instead of logging a
    // failure on every foreground.
    const staffId = await findPersonalStaffId();
    if (!staffId) return;
    // Only a device this staff member opted in personally (PushOptIn →
    // push_subscribe wrote their own push_subscriptions row for this
    // endpoint). A shared floor iPad registers the same /sw-push.js endpoint
    // in location_push_subscriptions; a personal login there must never
    // repost it as a personal subscription.
    const { data: ownRows, error: ownError } = await supabase
      .from('push_subscriptions')
      .select('endpoint')
      .eq('endpoint', subscription.endpoint)
      .limit(1);
    if (ownError) throw ownError;
    if (!ownRows || ownRows.length === 0) return;

    const refreshed = JSON.parse(localStorage.getItem(REFRESHED_AT_KEY) ?? 'null') as unknown;
    if (
      typeof refreshed === 'object' &&
      refreshed !== null &&
      'staffId' in refreshed &&
      'at' in refreshed &&
      refreshed.staffId === staffId &&
      typeof refreshed.at === 'number' &&
      Number.isFinite(refreshed.at) &&
      refreshed.at > 0 &&
      Date.now() - refreshed.at < REFRESH_INTERVAL_MS
    ) return;

    const json = subscription.toJSON();
    const endpoint = json.endpoint;
    const p256dh = json.keys?.p256dh;
    const auth = json.keys?.auth;
    if (!endpoint || !p256dh || !auth) {
      throw new Error('push: subscription is missing endpoint or keys');
    }

    const { error } = await supabase.rpc('push_subscribe' as never, {
      p_endpoint: endpoint,
      p_p256dh: p256dh,
      p_auth: auth,
      p_user_agent: navigator.userAgent,
    } as never);
    if (error) throw error;

    localStorage.setItem(REFRESHED_AT_KEY, JSON.stringify({ staffId, at: Date.now() }));
  } catch (error) {
    console.error('[push] subscription refresh failed', error);
  }
}

export async function unsubscribeThisDevice(): Promise<void> {
  await getPersonalStaffId();

  if (!('serviceWorker' in navigator)) {
    throw new Error('push: this browser cannot unsubscribe from Web Push');
  }
  const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
  if (!registration) {
    throw new Error('push: no service worker registration to unsubscribe');
  }
  const sub = await registration.pushManager.getSubscription();
  if (!sub) {
    throw new Error('push: this device is not subscribed');
  }

  // Browser first, DB second: if the RPC fails afterwards the leftover row is
  // pruned by dispatch on the first 404/410. The reverse order leaves a live
  // endpoint the server no longer knows about.
  const endpoint = sub.endpoint;
  const ok = await sub.unsubscribe();
  if (!ok) throw new Error('push: browser refused to unsubscribe');

  const { error } = await supabase.rpc('push_unsubscribe' as never, {
    p_endpoint: endpoint,
  } as never);
  if (error) throw error;
}
